import { createComponentPreviewEntry } from '@wix/editor-elements-integrations';
import type {
  IComponentPreviewWrapper,
  PreviewWrapperProps,
} from '@wix/editor-elements-types/thunderboltPreview';
import React, { useEffect, useState } from 'react';
import { PaginationType } from '../constants';
import type {
  IGridProps,
  IGridImperativeActions,
  IGridPreviewMapperProps,
} from '../Grid.types';

export function withComponentPreview(
  WrappedComponent: React.ComponentType<IGridProps>,
): IComponentPreviewWrapper<IGridProps, IGridPreviewMapperProps> {
  return React.forwardRef<IGridImperativeActions, IGridProps>(
    (
      {
        previewWrapperProps = {},
        ...viewerProps
      }: PreviewWrapperProps<IGridProps, IGridPreviewMapperProps>,
      ref,
    ) => {
      const {
        componentViewMode,
        initialRows = [],
        initialColumns = [],
      } = previewWrapperProps;
      const {
        userSelectionType,
        onChangeSelectedRow,
        onChangeSelectedCell,
        pagination,
        onChangePage,
        rows,
        columns,
      } = viewerProps;
      const [previousViewMode, setPreviousViewMode] = useState<
        string | undefined
      >(componentViewMode);

      useEffect(() => {
        if (previousViewMode !== 'editor' && componentViewMode === 'editor') {
          // Reset selection
          if (userSelectionType === 'row') {
            onChangeSelectedRow(undefined);
          } else if (userSelectionType === 'cell') {
            onChangeSelectedCell(undefined);
          }

          // Reset pagination
          const hasPagination = pagination.type === PaginationType.Pages;
          if (hasPagination) {
            onChangePage(1);
          }
        }
        setPreviousViewMode(componentViewMode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [componentViewMode]);

      return (
        <WrappedComponent
          {...viewerProps}
          ref={ref}
          rows={rows || initialRows}
          columns={
            Array.isArray(columns) && columns.length ? columns : initialColumns
          }
        />
      );
    },
  );
}

export default (ViewerComponent: React.ComponentType<IGridProps>) =>
  createComponentPreviewEntry(withComponentPreview(ViewerComponent));
